import { components as c } from "@/components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useTrackerQuery } from "@/features/trackers/api/selectTracker";
import { useUpdateTracker } from "@/features/trackers/api/updateTracker";
import { useInsertSharedAttributes } from "@/features/shared_attributes/api/insertSharedAttributes";
import { Button } from "@patient-watch/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const sharedAttributeSchemaZ = z.object({
    reference_date: z.string(),
});

type SharedAttributeSchemaT = z.infer<typeof sharedAttributeSchemaZ>;

const TrackerDateForm = () => {
    const { diaryId, trackerId } = Route.useParams();
    const { data: tracker, isLoading: trackerLoading } = useTrackerQuery(trackerId);
    const updateTracker = useUpdateTracker({ diaryId });
    const insertSharedAttribute = useInsertSharedAttributes();
    const navigate = useNavigate();

    // Form for picking a new date
    const form = useForm<SharedAttributeSchemaT>({
        resolver: zodResolver(sharedAttributeSchemaZ),
        defaultValues: {
            reference_date: new Date().toISOString(),
        },
    });

    if (trackerLoading || !tracker) {
        return <div>Loading...</div>;
    }

    // Get the questionnaire name for display
    const questionnaireName = tracker.form?.form_name || "questionnaire";

    // Generate a new event ID for potential event creation
    const newEventId = crypto.randomUUID();

    // Handle creating event and linking
    const handleCreateEvent = () => {
        // First create the shared attribute, then navigate to event creation with that shared_attribute_id
        insertSharedAttribute.mutate(
            { reference_date: form.getValues("reference_date"), diary_id: diaryId },
            {
                onSuccess: (newSharedAttr) => {
                    if (!newSharedAttr?.shared_attribute_id) return;
                    // Update the tracker to use this shared attribute
                    updateTracker.mutate({
                        tracker_id: trackerId,
                        shared_attribute_id: newSharedAttr.shared_attribute_id,
                    });
                    // Navigate to event creation with the shared_attribute_id
                    navigate({
                        to: "/templates/diary/$diaryId/events/$eventId",
                        params: { diaryId, eventId: newEventId },
                        search: { shared_attribute_id: newSharedAttr.shared_attribute_id },
                    });
                },
            }
        );
    };

    const handleCreateNewDate = (data: SharedAttributeSchemaT) => {
        insertSharedAttribute.mutate(
            { reference_date: data.reference_date, diary_id: diaryId },
            {
                onSuccess: (newSharedAttr) => {
                    if (!newSharedAttr?.shared_attribute_id) return;
                    updateTracker.mutate({
                        tracker_id: trackerId,
                        shared_attribute_id: newSharedAttr.shared_attribute_id,
                    });
                    navigate({ to: `/patients/${diaryId}` });
                },
            }
        );
    };

    return (
        <div className="space-y-6">
            <c.ui.Card>
                <c.ui.CardHeader>
                    <c.ui.CardTitle>Choose Reference Date</c.ui.CardTitle>
                </c.ui.CardHeader>
                <c.ui.CardContent>
                    <p className="text-base mb-6">
                        When do you want to start measuring {questionnaireName} scores from?
                    </p>
                    <c.ui.Form {...form}>
                        <c.fc.DateTime name="reference_date" label="Reference date" />
                    </c.ui.Form>
                </c.ui.CardContent>
            </c.ui.Card>

            <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Would you like to record a treatment on this date?</p>
                <div className="flex flex-col gap-2">
                    <Button type="button" size="lg" className="w-full" onClick={handleCreateEvent}>
                        Yes, add a treatment (injection, surgery, etc.)
                    </Button>
                    <Button
                        type="button"
                        variant="secondary"
                        size="lg"
                        className="w-full"
                        onClick={form.handleSubmit(handleCreateNewDate)}
                    >
                        No, just use this date
                    </Button>
                </div>
            </div>
        </div>
    );
};

export const Route = createFileRoute("/_app/templates/diary/$diaryId/trackers/$trackerId/date")({
    component: TrackerDateForm,
});
